import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import { Territory } from '@user/store/territory/typings'
import {
  overlapsViewport,
  ViewportBounds,
} from '@user/store/territory/boundsOverlap'
import { TERRITORY_FILL_LAYER } from './mapLayerIds'
import { TerritorySelection } from './useTerritoryInteraction'

/**
 * Below this zoom a territory is a smudge and its name is noise. Roughly town level -- the
 * point at which a boundary is big enough on screen to be worth naming.
 */
const MIN_LABEL_ZOOM = 9

const LABEL_CLASS = 'territory-label'

type Params = {
  map: maplibregl.Map | null
  territories: Territory[]
  /** Hidden while drawing: a label under the pen is in the way, not helpful. */
  enabled: boolean
  /**
   * Opens the territory's summary. Given, the name becomes the handle for its territory --
   * see the note on the map click below.
   */
  onSelect?: (selection: TerritorySelection) => void
}

/**
 * Territory names on the map (WWM-17508).
 *
 * DOM markers rather than a MapLibre symbol layer, and not by preference. The base style is
 * HERE raster tiles with no `glyphs` endpoint, and MapLibre cannot rasterise `text-field`
 * without one -- a text layer would silently render nothing. maplibregl.Marker needs no glyphs
 * and already has a precedent here in the locate-me dot.
 *
 * The cost of that choice is that MapLibre's label collision detection does not apply, so two
 * overlapping territories can produce two overlapping names. Kept in check by labelling only
 * what is on screen and only above a zoom where boundaries are legible, which on a realistic
 * map is a handful of labels rather than the thousand territories the NFR allows.
 *
 * Placed at the centre of the bounding box, not the polygon's centroid. The bounds are already
 * on the summary -- no geometry fetch, so a territory whose shape has not loaded is still
 * labelled. For a concave territory that centre can fall outside the shape; a true
 * pole-of-inaccessibility would fix it and needs the geometry, which is a trade worth making
 * only if it looks wrong in practice.
 *
 * **The name is also the handle: clicking it opens the territory's summary.** The polygon
 * itself deliberately does not, because a plain click on the map is how a rep adds a lead at an
 * address -- that flow reverse-geocodes first, so its bubble arrives a beat later and a
 * territory bubble opened on the same click lands on top of it. A territory covers every
 * address inside it, so the two can only be told apart by what was aimed at, and the address is
 * the smaller, more deliberate target. The marker's own listeners stop the event reaching the
 * canvas, which is what keeps the two gestures separate.
 */
export function useTerritoryLabels({
  map,
  territories,
  enabled,
  onSelect,
}: Params): void {
  const markersRef = useRef<Map<number, maplibregl.Marker>>(new Map())

  // Both read from inside the label's own listeners, which are attached once per marker: a
  // dependency would mean rebuilding every label whenever the parent re-rendered.
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  const centresRef = useRef<Map<number, [number, number]>>(new Map())

  useEffect(() => {
    if (!map) return

    const markers = markersRef.current

    const clearAll = () => {
      markers.forEach((marker) => marker.remove())
      markers.clear()
      centresRef.current.clear()
    }

    if (!enabled) {
      clearAll()
      return
    }

    /**
     * The name's own listeners.
     *
     * Every one of them stops the event: markers are appended to the map's canvas container, so
     * without this a click on the name would also reach the map underneath and start the
     * add-lead reverse-geocode, and a press would begin a pan.
     */
    const attachLabelHandlers = (element: HTMLElement, id: number) => {
      element.addEventListener('mousedown', (event) => event.stopPropagation())
      element.addEventListener('touchstart', (event) => event.stopPropagation())
      element.addEventListener('mouseenter', () => {
        element.style.textDecoration = 'underline'
      })
      element.addEventListener('mouseleave', () => {
        element.style.textDecoration = 'none'
      })
      element.addEventListener('click', (event) => {
        event.stopPropagation()

        const select = onSelectRef.current
        const centre = centresRef.current.get(id)
        if (!select || !centre) return

        // The same overlap list a click on the shape produces, read at the label's own point:
        // territories may overlap, and the bubble says which others cover this spot.
        const others = map.getLayer(TERRITORY_FILL_LAYER)
          ? map
              .queryRenderedFeatures(map.project(centre), {
                layers: [TERRITORY_FILL_LAYER],
              })
              .map((feature) => Number(feature.id))
              .filter((other) => other !== id)
          : []

        select({
          territoryId: id,
          lngLat: { lng: centre[0], lat: centre[1] },
          otherTerritoryIds: others,
        })
      })
    }

    const render = () => {
      const zoom = map.getZoom()

      if (zoom < MIN_LABEL_ZOOM) {
        clearAll()
        return
      }

      const bounds = map.getBounds()
      const viewport: ViewportBounds = {
        minLatitude: bounds.getSouth(),
        maxLatitude: bounds.getNorth(),
        minLongitude: bounds.getWest(),
        maxLongitude: bounds.getEast(),
      }

      const visible = new Set<number>()

      for (const territory of territories) {
        if (!overlapsViewport(territory.bounds, viewport)) continue

        visible.add(territory.id)

        const centre: [number, number] = [
          (territory.bounds.minLongitude + territory.bounds.maxLongitude) / 2,
          (territory.bounds.minLatitude + territory.bounds.maxLatitude) / 2,
        ]
        centresRef.current.set(territory.id, centre)

        const existing = markers.get(territory.id)

        if (existing) {
          // Moved rather than recreated: a redraw changes the bounds, and tearing the element
          // down would make the label flicker on every pan that touches it.
          existing.setLngLat(centre)
          existing.getElement().textContent = territory.name
          continue
        }

        const element = document.createElement('div')
        element.className = LABEL_CLASS
        element.textContent = territory.name
        element.dataset.territoryId = String(territory.id)
        // Inline rather than a stylesheet so the label cannot be affected by, or affect,
        // anything else on the map canvas.
        element.style.cssText = [
          'font: 600 12px/1.2 system-ui, sans-serif',
          'color: #1a1a1a',
          // A halo, not a background box: the territory fill beneath must stay visible, and a
          // solid chip would hide the very boundary the name describes.
          'text-shadow: 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff',
          'white-space: nowrap',
          // Interactive only when there is something to open. Without a handler the label must
          // stay inert, or it would swallow clicks meant for the map beneath it.
          onSelectRef.current ? 'pointer-events: auto' : 'pointer-events: none',
          onSelectRef.current ? 'cursor: pointer' : '',
          'user-select: none',
        ].join(';')

        if (onSelectRef.current) attachLabelHandlers(element, territory.id)

        markers.set(
          territory.id,
          new maplibregl.Marker({ element }).setLngLat(centre).addTo(map),
        )
      }

      // Drop labels for territories that have scrolled away or been deleted.
      for (const [id, marker] of markers) {
        if (!visible.has(id)) {
          marker.remove()
          markers.delete(id)
          centresRef.current.delete(id)
        }
      }
    }

    render()

    // moveend, not move: repositioning every label on every animation frame of a pan is work
    // the browser does not need to do, and the labels travel with the map anyway.
    map.on('moveend', render)
    map.on('zoomend', render)

    return () => {
      map.off('moveend', render)
      map.off('zoomend', render)
      clearAll()
    }
  }, [map, territories, enabled])
}
